import { useQuery } from '@tanstack/react-query';
import type { AxiosInstance } from 'axios';
import { SilentError } from 'init/silent-error';
import {
  type ApiError,
  addResourceNameToQueryResult,
  handleZodError,
  type QueryOptions,
  useAxiosInstance,
} from 'shared/hooks/utils';
import { z } from 'zod/v4';

const URL = '/invoice_logs';

// A single invoicing log ("journal de facturation"): a prefix plus the counter
// that will feed the next invoice reference. `counter` is the value shown to the
// user (the stored counter minus one); `deletable` is false when invoices are
// already attached to the log.
export const InvoiceLogSchema = z.object({
  id: z.string(),
  prefix: z.string(),
  counter: z.number(),
  deletable: z.boolean(),
});

const InvoiceLogsSchema = z.object({
  invoiceLogs: z.array(InvoiceLogSchema),
});

export type InvoiceLog = z.infer<typeof InvoiceLogSchema>;

export async function loadInvoiceLogsQueryFn(axiosInstance: Promise<AxiosInstance>) {
  const instance = await axiosInstance;
  const { data } = await instance.get(URL);

  const parseResult = InvoiceLogsSchema.safeParse(data);
  if (!parseResult.success) {
    handleZodError(parseResult, 'invoiceLogs');
    throw new SilentError();
  }

  return parseResult.data.invoiceLogs;
}

export function useLoadInvoiceLogs(options: QueryOptions<InvoiceLog[]> = {}) {
  const axiosInstance = useAxiosInstance();

  const queryResult = useQuery<InvoiceLog[], ApiError>({
    queryKey: ['invoiceLogs'],
    queryFn: () => loadInvoiceLogsQueryFn(axiosInstance),
    ...options,
  });

  return addResourceNameToQueryResult<InvoiceLog[], ApiError, 'invoiceLogs'>(
    'invoiceLogs',
    queryResult,
  );
}
